import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Plus, Send, ArrowLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import Modal, { TextField, TextArea } from "@/components/Modal";
import { toast } from "sonner";

const HOME_FOR_ROLE = { professional: "/pro", livreur: "/livreur", influencer: "/influenceur" };
const ROLE_LABEL = { client: "Client", professional: "Professionnel", livreur: "Livreur", influencer: "Influenceur" };

export default function MessagingPage() {
  const { user } = useAuth();
  const home = HOME_FOR_ROLE[user?.role] || "/";
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => api.get("/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([])), []);
  useEffect(() => { if (user) load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [user, load]);

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <header className="sticky top-0 z-30 bg-[#0F172A] text-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={home} className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition" data-testid="messaging-back"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#E08B00]" />
            <div><p className="font-display font-extrabold leading-none">Messagerie</p><p className="text-[11px] text-slate-400">Échangez avec l'administration</p></div>
          </div>
          <button onClick={() => setShowNew(true)} className="ml-auto bg-[#C85A32] text-white font-bold text-sm px-3 py-2 rounded-lg inline-flex items-center gap-1" data-testid="new-conversation-btn"><Plus className="w-4 h-4" /> Nouveau</button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-5">
        {!convs ? <Loader /> : convs.length === 0 ? (
          <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-xl">
            <MessageSquare className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-400 text-sm">Aucune conversation.</p>
            <button onClick={() => setShowNew(true)} className="mt-3 text-[#C85A32] font-semibold text-sm" data-testid="empty-new-conversation">Écrire à l'administration →</button>
          </div>
        ) : (
          <div className="space-y-2">
            {convs.map((c) => (
              <button key={c.id} onClick={() => setActive(c.id)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#C85A32] transition relative" data-testid={`conversation-${c.id}`}>
                {c.unread_user && <span className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full bg-[#C85A32]" data-testid={`unread-${c.id}`} />}
                <div className="flex items-center gap-2">
                  <span className="font-num text-xs text-slate-400">{c.conversation_number}</span>
                  <span className="ml-auto text-xs text-slate-400 mr-4">{formatDate(c.last_at)}</span>
                </div>
                <p className="font-semibold mt-1 pr-6">{c.subject}</p>
                <p className="text-sm text-slate-500 line-clamp-1">{c.last_message}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {showNew && <NewConversation onClose={() => setShowNew(false)} onDone={load} />}
      {active && <ConversationThread id={active} onClose={() => setActive(null)} onChanged={load} />}
    </div>
  );
}

function NewConversation({ onClose, onDone }) {
  const [f, setF] = useState({ subject: "", message: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/conversations", f); toast.success("Message envoyé à l'administration"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title="Nouvelle conversation" onClose={onClose} testid="conversation-form">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Sujet" value={f.subject} onChange={set("subject")} required testid="conversation-subject" />
        <TextArea label="Message" value={f.message} onChange={set("message")} required testid="conversation-message" rows={4} />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="conversation-submit">Envoyer</button>
      </form>
    </Modal>
  );
}

export function ConversationThread({ id, onClose, onChanged, staff }) {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [msg, setMsg] = useState("");
  const [templates, setTemplates] = useState([]);
  const load = useCallback(() => api.get(`/conversations/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (staff) api.get("/message-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, [staff]);

  const send = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/conversations/${id}/messages`, { message: msg }); setMsg(""); load(); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title={c ? c.subject : "Conversation"} onClose={onClose} testid="conversation-thread" size="lg">
      {!c ? <Loader /> : (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">{c.conversation_number}{staff ? ` · ${c.user_name} (${ROLE_LABEL[c.user_role] || c.user_role})` : ""}</p>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {(c.messages || []).map((m, i) => {
              const mine = m.author_id === user?.user_id;
              return (
                <div key={i} className={`rounded-lg p-3 ${mine ? "bg-[#FDF3EE] ml-6" : "bg-slate-50 mr-6"}`} data-testid={`conversation-msg-${i}`}>
                  <p className="text-xs text-slate-400 mb-1">{m.author_name}{m.is_staff ? " · TOGOBATI" : ""} · {formatDate(m.at)}</p>
                  <p className="text-sm">{m.message}</p>
                </div>
              );
            })}
          </div>
          {staff && templates.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="template-chips">
              {templates.map((t) => (
                <button key={t.id} type="button" onClick={() => setMsg(t.body)} title={t.body} className="text-[11px] bg-slate-100 hover:bg-[#FDF3EE] border border-slate-200 rounded-full px-2.5 py-1 transition" data-testid={`template-${t.id}`}>{t.title}</button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message…" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="conversation-reply-input" />
            <button onClick={send} className="bg-[#C85A32] text-white px-4 rounded-lg inline-flex items-center gap-1" data-testid="conversation-reply-send"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </Modal>
  );
}
